import type React from 'react';

import { Link } from '@react-spectrum/s2';

import { useNavigate } from 'react-router';
import type { z } from 'zod';

/**
 * Creates a type-safe route with validation, link component, and navigation hook
 *
 * NOTE: This is AI generated xD
 */
export function makeRoute<T extends z.ZodRawShape>(config: {
	path: string;
	params: z.ZodObject<T>;
}) {
	const { path, params } = config;

	// Type for the validated parameters
	type Params = z.infer<typeof params>;

	// Generate path with parameters
	const getPath = (paramValues: Params): string => {
		let result = path;

		// Replace path parameters like :id with actual values
		Object.entries(paramValues).forEach(([key, value]) => {
			result = result.replace(`:${key}`, String(value));
		});

		return result;
	};

	// Validate and generate path
	const getValidatedPath = (paramValues: unknown): string => {
		const validated = params.parse(paramValues);
		return getPath(validated);
	};

	// Link component
	const RouteLink = ({
		children,
		params: linkParams,
		...linkProps
	}: {
		children: React.ReactNode;
		params: Params;
	} & Omit<React.ComponentProps<typeof Link>, 'href'>) => {
		const href = getValidatedPath(linkParams);
		return (
			<Link href={href} {...linkProps}>
				{children}
			</Link>
		);
	};

	// Navigation hook
	const useRouteNavigate = () => {
		const navigate = useNavigate();
		return (paramValues: Params) => {
			const path = getValidatedPath(paramValues);
			navigate(path);
		};
	};

	// Parse parameters from current path
	const parseParams = (currentPath: string): Params | null => {
		try {
			// Simple parameter extraction (this could be enhanced for more complex patterns)
			const pathParts = path.split('/');
			const currentParts = currentPath.split('/');

			if (pathParts.length !== currentParts.length) {
				return null;
			}

			const extractedParams: Record<string, string> = {};

			pathParts.forEach((part, index) => {
				if (part.startsWith(':')) {
					const paramName = part.slice(1);
					extractedParams[paramName] = currentParts[index];
				} else if (part !== currentParts[index]) {
					throw new Error('Path does not match');
				}
			});

			return params.parse(extractedParams);
		} catch {
			return null;
		}
	};

	return {
		path,
		params,
		getPath,
		getValidatedPath,
		Link: RouteLink,
		useNavigate: useRouteNavigate,
		parseParams,
	};
}
